import React, { useState } from 'react';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import OrgDataPoolDialog from './OrgDataPoolDialog';
import OrgDataPools from './OrgDataPools';
import OrgClients from './OrgClients';
import ClientDialog from './ClientDialog';
import OrgTokenizerDialog from './OrgTokenizerDialog';
import { AppContext } from '../../App';
import usePaginatedDataPools from '../hooks/usePaginatedDataPools';
import useClients from '../hooks/useClients';
import { btnSubtleNeutralSx } from '../common/filterStyles';

const btnOrgPrimarySx = { ...btnSubtleNeutralSx, borderColor: '#1D9AAA', color: '#1D9AAA' };

function OrgMainContent() {
  const { org, orgId, orgLabel } = React.useContext(AppContext);
  const [activeTab, setActiveTab] = useState(0);
  const [showDataPoolDialog, setShowDataPoolDialog] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const dataPoolsQuery = usePaginatedDataPools(orgId, 100, 'label', !!orgId);
  const clientsQuery = useClients(orgId);

  if (!org) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#F5F6FA',
        }}
      >
        <AccountTreeOutlinedIcon sx={{ fontSize: 56, mb: 2, color: '#BCD3D2' }} />
        <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#1a2332' }}>
          Select an Organization
        </Typography>
      </Box>
    );
  }

  const poolCount = dataPoolsQuery.data?.count ?? 0;
  const dataPoolNames = dataPoolsQuery.data?.results?.map((dataPool) => dataPool.label) ?? [];

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: '#F5F6FA',
        padding: '28px 40px 40px',
        overflowY: 'auto',
        minWidth: 0,
      }}
    >
      {/* Content Header */}
      <Box
        id="org-info"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            data-cy="org-title"
            sx={{
              fontSize: '22px',
              fontWeight: 700,
              letterSpacing: '-0.3px',
              color: '#1a2332',
            }}
          >
            {orgLabel}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#5f6d7e', mt: 0.5 }}>
            {poolCount} data pool{poolCount !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* Action buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            '& .MuiButton-outlined': btnSubtleNeutralSx,
          }}
        >
          <OrgTokenizerDialog />
          <Button
            sx={btnOrgPrimarySx}
            startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => activeTab === 0 ? setShowDataPoolDialog(true) : setShowClientDialog(true)}
            data-cy={activeTab === 0 ? 'btn-create-data-pool-open' : 'btn-create-client-open'}
          >
            Create
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.08)', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          data-cy="org-tabs"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              px: 2,
              py: 0.75,
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              color: '#5f6d7e',
              letterSpacing: '0.2px',
              '&:hover': { color: '#1a2332' },
              '&.Mui-selected': { color: '#1D9AAA', fontWeight: 600 },
            },
            '& .MuiTabs-indicator': {
              height: 2,
              borderRadius: '2px 2px 0 0',
              backgroundColor: '#1D9AAA',
            },
          }}
        >
          <Tab label="Data Pools" data-cy="tab-data-pools" />
          <Tab label="Clients" data-cy="tab-clients" />
        </Tabs>
      </Box>

      {/* New Data Pool Dialog */}
      <OrgDataPoolDialog
        open={showDataPoolDialog}
        onClose={() => setShowDataPoolDialog(false)}
        orgId={orgId}
        dataPoolNames={dataPoolNames}
      />

      {/* New Client Dialog */}
      <ClientDialog
        open={showClientDialog}
        mode="create"
        orgId={orgId}
        existingClients={clientsQuery.data}
        onClose={() => setShowClientDialog(false)}
      />

      {/* Tab Content */}
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.04)',
        }}
      >
        {activeTab === 0 && <OrgDataPools {...dataPoolsQuery} />}
        {activeTab === 1 && (
          <OrgClients
            clients={clientsQuery.data}
            isLoading={clientsQuery.isLoading}
            isError={clientsQuery.isError}
            orgId={orgId}
          />
        )}
      </Box>
    </Box>
  );
}

export default OrgMainContent;
